/**
 * Service to get student data for parent dashboard
 * This service helps parents view their child's performance data
 */
import { supabase } from './supabase';
import { getApiUrl } from './apiConfig';

export const getStudentByName = async (studentName) => {
  try {
    console.log('🔍 getStudentByName called with:', studentName);
    
    // Get JWT token for authentication
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    
    if (sessionError || !session?.access_token) {
      console.error('No valid session found:', sessionError);
      return null;
    }
    
    // First, get all enrolled students to find the one matching the name
    const response = await fetch(getApiUrl('enrolled-students'), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`
      }
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    console.log('📊 All enrolled students:', data);
    
    if (data && data.students) {
      // Match the full name exactly (ignoring case and extra spaces) so a partial
      // name like "Rao" can't pick up some other student
      const normalize = (name) => (name || '').trim().replace(/\s+/g, ' ').toLowerCase();
      const searchName = normalize(studentName);
      if (!searchName) {
        return null;
      }
      const matches = data.students.filter(s => normalize(s.name) === searchName);

      if (matches.length === 1) {
        console.log('✅ Found student for parent:', matches[0].name, matches[0].user_id);
        return matches[0];
      }
      if (matches.length > 1) {
        // Several students share this name — don't guess which child it is
        console.warn(`⚠️ ${matches.length} students are named "${studentName}"; not linking any of them`);
        return null;
      }
      console.warn('⚠️ No student found with name:', studentName);
      return null;
    }
    
    return null;
  } catch (error) {
    console.error('❌ Error fetching student by name:', error);
    return null;
  }
};

export const getStudentSubjectScoresForParent = async (studentUserId) => {
  try {
    // Get JWT token for authentication
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    
    if (sessionError || !session?.access_token) {
      console.error('No valid session found:', sessionError);
      return null;
    }
    
    const response = await fetch(getApiUrl(`student-subject-scores?user_id=${studentUserId}`), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`
      }
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('❌ Error fetching student subject scores for parent:', error);
    return null;
  }
};

export const getStudentTopicMasteryForParent = async (studentUserId) => {
  try {
    // Get JWT token for authentication
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    
    if (sessionError || !session?.access_token) {
      console.error('No valid session found:', sessionError);
      return null;
    }
    
    // Use the student-specific topic mastery endpoint (similar to student-subject-scores)
    const response = await fetch(getApiUrl(`student-topic-mastery?user_id=${studentUserId}`), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`
      }
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('❌ Error fetching student topic mastery for parent:', error);
    return null;
  }
};
